import type { ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import type { AvailabilityCalendarDay } from './availabilityCalendarMeta'

/**
 * Modal revealing why an annotated (greyed-but-clickable) calendar day is
 * unavailable. Open while a day is provided; closing clears the selection.
 * An optional `action` adds a call-to-action in the footer (e.g. a high-season
 * wish link for season-closed days).
 */
export function AnnotatedDayDialog({
  day,
  title,
  dateLabel,
  closeLabel,
  onClose,
  action,
}: {
  day: AvailabilityCalendarDay | null
  title: string
  /** Pre-formatted, human-readable date for the open day. */
  dateLabel?: string
  closeLabel: string
  onClose: () => void
  /** Optional CTA rendered before the close button (e.g. link to the wish flow). */
  action?: ReactNode
}) {
  return (
    <Dialog open={day !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {dateLabel ? <DialogDescription>{dateLabel}</DialogDescription> : null}
        </DialogHeader>
        <p className="text-sm text-foreground">{day?.noteText ?? day?.label}</p>
        <DialogFooter>
          {action}
          <Button type="button" variant="outline" onClick={onClose}>
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
